// O Editor do Mentor, aba Série (Círculo, parte 4, 15/10/2026). O desenho é
// docs/circulo-mentor/prototipo/editor-do-mentor.html (formSerie, .card-serie).
//
// A lista das séries do Mentor (capa mini, título, livro, nº de episódios e
// status) e o formulário de uma série: a foto do exemplar (recorte 2:3, o
// mesmo fluxo da foto do perfil), o livro (da estante dele, do catálogo ou
// digitado), título, descrição, link da loja, cadência e status. Depois de
// salva, os episódios: adicionar, editar título e sinopse, subir, descer e
// apagar com confirmação. A página em que cada um abre, o vídeo e o publicar
// ficam no Estúdio.
//
// No assistente "Montar meu Círculo" (prop `assistente`), a mesma tela é o
// passo da 1ª série: abre direto no formulário (a série dada ou uma nova), sem
// a lista, e o Avançar do rodapé do passo grava e segue.
//
// REGRA DO WESLEY: o Mentor monta tudo. O título da série começa vazio e
// nunca vem do nome do livro; o rótulo basta, sem dicas. A descrição e o link
// da loja são opcionais: fora do assistente, ficam em "Mais detalhes"
// recolhido (TELA LIMPA, 18/10; ver editor.tsx). Nada é cortado:
// passou do limite, o contador avisa e a API devolve o motivo (400 { error,
// campo }), que aparece junto do campo. API: server/circulo/editor-serie.js.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import { Ic, Recorte, abrirImagem, type RodapePasso } from './editor-comum';

type Livro = { book_id: string | null; titulo: string; autor: string; paginas: string };
type Rasc = { titulo: string; descricao: string; loja_url: string; cadencia: string; status: string; cover_url: string | null; livro: Livro | null };
type Modo = 'estante' | 'catalogo' | 'manual';
type Achado = { book_id?: string; titulo: string; autor: string | null; paginas: number | null; editora?: string | null; ano?: string | null };

const LIM = { titulo: 90, descricao: 400, livro_titulo: 200, livro_autor: 120, loja_url: 500, episodio_titulo: 140, episodio_sinopse: 600 };
const CADENCIAS: [string, string][] = [
  ['semanal', 'Um episódio por semana'],
  ['quinzenal', 'A cada duas semanas'],
  ['mensal', 'Um por mês'],
  ['', 'Sem cadência fixa'],
];
const STATUS: [string, string][] = [
  ['rascunho', 'Rascunho · só você vê'],
  ['publicando', 'Publicando · na sua página'],
  ['concluida', 'Concluída · na sua página'],
];
const STATUS_CURTO: Record<string, string> = { rascunho: 'rascunho', publicando: 'publicando', concluida: 'concluída' };

const contar = (s: string) => Array.from(s).length;
const VAZIA: Rasc = { titulo: '', descricao: '', loja_url: '', cadencia: 'semanal', status: 'rascunho', cover_url: null, livro: null };
const rascDe = (s: any): Rasc => ({
  titulo: s.titulo || '',
  descricao: s.descricao || '',
  loja_url: s.loja_url || '',
  cadencia: s.cadencia || '',
  status: s.status || 'rascunho',
  cover_url: s.cover_url || null,
  livro: s.livro?.titulo ? { book_id: s.livro.book_id || null, titulo: s.livro.titulo, autor: s.livro.autor || '', paginas: s.livro.paginas ? String(s.livro.paginas) : '' } : null,
});
const livroParaApi = (l: Livro) => (l.book_id
  ? { book_id: l.book_id }
  : { titulo: l.titulo.trim(), autor: l.autor.trim() || null, paginas: l.paginas.trim() ? Number(l.paginas) : null });
const mesmoLivro = (a: Livro | null, b: Livro | null) => JSON.stringify(a && livroParaApi(a)) === JSON.stringify(b && livroParaApi(b));

// Só o que mudou (ao editar); tudo o que tem valor (ao criar).
function diferencas(base: Rasc | null, r: Rasc): Record<string, any> {
  const d: Record<string, any> = {};
  const txt = (k: 'titulo' | 'descricao' | 'loja_url') => { const v = r[k].trim(); if (!base || v !== base[k].trim()) d[k] = v || null; };
  txt('titulo'); txt('descricao'); txt('loja_url');
  if (!base || r.cadencia !== base.cadencia) d.cadencia = r.cadencia || null;
  if (!base || r.status !== base.status) d.status = r.status;
  if (!base ? r.cover_url : r.cover_url !== base.cover_url) d.cover_url = r.cover_url;
  if (r.livro && (!base || !mesmoLivro(base.livro, r.livro))) d.livro = livroParaApi(r.livro);
  return d;
}

// A capa: a foto do exemplar ou, sem ela, título e autor tipográficos (tokens).
const CapaMini: React.FC<{ url: string | null; titulo?: string; grande?: boolean }> = ({ url, titulo, grande }) => (
  <span className={`ced-capa${grande ? ' ced-capa-g' : ''}`} data-capa={url ? 'foto' : 'tipografica'} aria-hidden="true">
    {url ? <img src={url} alt="" /> : <span>{titulo || ''}</span>}
  </span>
);

export const AbaSerie: React.FC<{
  session: any;
  onNaoEMentor: () => void;
  onMudou?: () => void; // a contagem da Visão geral
  rolarTopo?: () => void;
  assistente?: { serieId: string | null; rodape: RodapePasso; onAvancou: (serieId: string) => void };
}> = ({ session, onNaoEMentor, onMudou, rolarTopo, assistente }) => {
  const [lista, setLista] = useState<any[] | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [aberta, setAberta] = useState<string | 'nova' | null>(null);
  const [base, setBase] = useState<Rasc | null>(null);
  const [rasc, setRasc] = useState<Rasc>(VAZIA);
  const [eps, setEps] = useState<any[]>([]);
  const [lim, setLim] = useState(LIM);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [carregandoSerie, setCarregandoSerie] = useState(false);
  const [escolhendo, setEscolhendo] = useState(true);
  const [modo, setModo] = useState<Modo>('estante');
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<Achado[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [manual, setManual] = useState<Livro>({ book_id: null, titulo: '', autor: '', paginas: '' });
  const [recorte, setRecorte] = useState<{ url: string; img: HTMLImageElement } | null>(null);
  const [enviandoCapa, setEnviandoCapa] = useState(false);
  const [novoEp, setNovoEp] = useState('');
  const [editEp, setEditEp] = useState<{ id: string; titulo: string; sinopse: string } | null>(null);
  const [ocupadoEp, setOcupadoEp] = useState<string | null>(null);
  const [apagar, setApagar] = useState<any | null>(null);
  const [maisAberto, setMaisAberto] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);
  const naoEMentor = useRef(onNaoEMentor);
  naoEMentor.current = onNaoEMentor;

  const erroDaApi = (e: any, padrao = 'Não deu para salvar agora. Confira a conexão e tente de novo.') =>
    (e?.status === 400 || e?.status === 409 || e?.status === 413 ? (e?.dados?.error || e.message) : padrao);
  const tratar403 = (e: any) => { if (e?.status === 403 || e?.status === 401) { naoEMentor.current(); return true; } return false; };

  const carregarLista = useCallback(async () => {
    setFalha(null);
    try {
      const d = await api.getSeriesEditor(session);
      setLista(d.series || []);
      if (d.limites) setLim((l) => ({ ...l, ...d.limites }));
    } catch (e: any) {
      if (tratar403(e)) return;
      setFalha('Não deu para abrir as séries agora. Confira a conexão e tente de novo.');
    }
  }, [session]);
  // No assistente não há lista: abre direto a série do passo (ou uma nova).
  const serieDoPasso = assistente ? assistente.serieId || 'nova' : null;
  useEffect(() => { if (!serieDoPasso) carregarLista(); }, [carregarLista, serieDoPasso]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (serieDoPasso) abrir(serieDoPasso); }, [serieDoPasso]);
  useEffect(() => { if (!salvo) return; const t = setTimeout(() => setSalvo(false), 4000); return () => clearTimeout(t); }, [salvo]);

  const aplicar = (d: any) => {
    setBase(rascDe(d.serie));
    setRasc(rascDe(d.serie));
    setEps(d.episodios || []);
    if (d.limites) setLim((l) => ({ ...l, ...d.limites }));
    setAberta(d.serie.id);
    setEscolhendo(!d.serie.livro?.titulo);
  };

  const abrir = async (id: string | 'nova') => {
    setErros({}); setSalvo(false); setEditEp(null); setNovoEp(''); setBusca(''); setAchados(null); setModo('estante');
    setManual({ book_id: null, titulo: '', autor: '', paginas: '' });
    rolarTopo?.();
    if (id === 'nova') { setAberta('nova'); setBase(null); setRasc(VAZIA); setEps([]); setEscolhendo(true); return; }
    setAberta(id); setCarregandoSerie(true);
    try { aplicar(await api.getSerieEditor(session, id)); } catch (e: any) {
      if (tratar403(e)) return;
      setAberta(null);
      setFalha('Não deu para abrir a série agora. Tente de novo.');
    } finally { setCarregandoSerie(false); }
  };
  const voltar = () => { setAberta(null); setErros({}); setSalvo(false); rolarTopo?.(); carregarLista(); };

  // ---- busca do livro (estante ou catálogo), com uma pausa enquanto digita ----
  useEffect(() => {
    if (!aberta || !escolhendo || modo === 'manual') return;
    const q = busca.trim();
    if (modo === 'catalogo' && q.length < 3) { setAchados(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        if (modo === 'estante') {
          const d = await api.buscarEstanteEditor(session, q);
          if (vivo) setAchados(d.livros || []);
        } else {
          const d = await api.searchBooks(session, q);
          if (vivo) setAchados((d.results || []).slice(0, 8).map((b) => ({
            titulo: b.title, autor: b.author || null, paginas: b.pages || null,
            editora: b.publisher || null, ano: (b.publishedDate || '').slice(0, 4) || null,
          })));
        }
      } catch (_) {
        if (vivo) setAchados([]);
      } finally { if (vivo) setBuscando(false); }
    }, modo === 'estante' ? 250 : 450);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, modo, escolhendo, aberta, session]);

  const mudar = <K extends keyof Rasc>(k: K, v: Rasc[K]) => {
    setRasc((r) => ({ ...r, [k]: v }));
    setSalvo(false);
    if (erros[k as string] || erros._geral) setErros((e) => { const n = { ...e }; delete n[k as string]; delete n._geral; return n; });
  };
  const escolherLivro = (l: Livro) => { mudar('livro', l); setEscolhendo(false); setBusca(''); setAchados(null); };
  const usarManual = () => {
    const t = manual.titulo.trim();
    if (!t) { setErros((e) => ({ ...e, livro: 'Escreva o título do livro.' })); return; }
    const p = manual.paginas.trim();
    if (p && (!/^\d+$/.test(p) || Number(p) < 1)) { setErros((e) => ({ ...e, livro: 'O número de páginas precisa ser um inteiro maior que zero.' })); return; }
    escolherLivro({ book_id: null, titulo: t, autor: manual.autor.trim(), paginas: p });
  };

  // ---- capa ----
  const escolherCapa = (f: File | undefined) => {
    if (!f) return;
    setErros((e) => { const n = { ...e }; delete n.cover_url; return n; });
    abrirImagem(f, setRecorte, (msg) => setErros((e) => ({ ...e, cover_url: msg })));
  };
  const fecharRecorte = () => { if (recorte) URL.revokeObjectURL(recorte.url); setRecorte(null); };
  const usarRecorte = async (blob: Blob) => {
    fecharRecorte();
    setEnviandoCapa(true);
    try {
      const r = await api.enviarCapaEditor(session, blob);
      mudar('cover_url', r.cover_url);
    } catch (e: any) {
      if (tratar403(e)) return;
      setErros((x) => ({ ...x, cover_url: e?.dados?.error || 'Não deu para enviar a foto. Tente de novo.' }));
    } finally { setEnviandoCapa(false); }
  };

  // ---- salvar a série ----
  const mudou = aberta ? diferencas(base, rasc) : {};
  const temMudanca = aberta === 'nova' ? true : Object.keys(mudou).length > 0;
  // Devolve o id da série gravada (ou null, se não gravou).
  const salvar = async (): Promise<string | null> => {
    if (salvando || !temMudanca) return null;
    if (!rasc.livro) { setErros({ livro: 'Escolha o livro da série: da sua estante, do catálogo ou digitado.' }); return null; }
    setSalvando(true); setSalvo(false); setErros({});
    try {
      const d = await api.salvarSerieEditor(session, aberta === 'nova' ? null : aberta, mudou);
      aplicar(d);
      setSalvo(true);
      onMudou?.();
      return d.serie.id;
    } catch (e: any) {
      if (tratar403(e)) return null;
      const campo = e?.status === 400 && e?.dados?.campo;
      setErros({ [campo || '_geral']: erroDaApi(e) });
      if (campo) document.getElementById(`ces-${campo}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return null;
    } finally { setSalvando(false); }
  };
  // O Avançar do assistente: grava o que mudou (a série nova, sempre) e segue.
  const avancarPasso = async () => {
    if (!assistente || salvando || !aberta) return;
    const id = temMudanca ? await salvar() : aberta;
    if (id && id !== 'nova') assistente.onAvancou(id);
  };

  // ---- episódios ----
  const comEp = async (id: string, f: () => Promise<any>) => {
    setOcupadoEp(id); setErros((e) => { const n = { ...e }; delete n.eps; return n; });
    try { const d = await f(); setEps(d.episodios || []); onMudou?.(); return true; } catch (e: any) {
      if (tratar403(e)) return false;
      setErros((x) => ({ ...x, eps: erroDaApi(e, 'Não deu para gravar agora. Confira a conexão e tente de novo.') }));
      return false;
    } finally { setOcupadoEp(null); }
  };
  const adicionarEp = async () => {
    const t = novoEp.trim();
    if (!t || !aberta || aberta === 'nova') return;
    if (await comEp('novo', () => api.criarEpisodioEditor(session, aberta, { titulo: t }))) setNovoEp('');
  };
  const gravarEp = async () => {
    if (!editEp) return;
    const ep = eps.find((x) => x.id === editEp.id);
    const dados: any = {};
    if (editEp.titulo.trim() !== (ep?.titulo || '')) dados.titulo = editEp.titulo;
    if (editEp.sinopse.trim() !== (ep?.sinopse || '')) dados.sinopse = editEp.sinopse.trim() || null;
    if (!Object.keys(dados).length) { setEditEp(null); return; }
    if (await comEp(editEp.id, () => api.editarEpisodioEditor(session, editEp.id, dados))) setEditEp(null);
  };
  const moverEp = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (!aberta || aberta === 'nova' || j < 0 || j >= eps.length) return;
    const ids = eps.map((x) => x.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    comEp(eps[i].id, () => api.ordenarEpisodiosEditor(session, aberta, ids));
  };
  const pedirApagar = (ep: any) => { setErros((e) => { const n = { ...e }; delete n.eps; return n; }); setApagar(ep); };
  const confirmarApagar = async () => {
    const ep = apagar;
    if (!ep) return;
    const ok = await comEp(ep.id, () => api.apagarEpisodioEditor(session, ep.id, true));
    if (ok) { setApagar(null); if (editEp?.id === ep.id) setEditEp(null); }
    else setApagar(null);
  };

  const contador = (n: number, l: number, k: string) =>
    <span className="ced-cnt" data-cnt={k} data-estado={n > l ? 'passou' : n >= l * 0.92 ? 'perto' : ''}>{n}/{l}</span>;
  const erroDe = (k: string) => erros[k] ? <p className="ced-erro" role="alert" data-erro={k}>{erros[k]}</p> : null;

  // ===== o assistente, enquanto a série abre =====
  if (serieDoPasso && (!aberta || carregandoSerie)) {
    return (
      <section className="ced-serie" data-vista-serie="">
        {falha
          ? <div className="ced-aviso"><p>{falha}</p><button className="ced-btn ced-btn-2" onClick={() => { setFalha(null); abrir(serieDoPasso); }}>Tentar de novo</button></div>
          : <p className="ced-nota" aria-busy="true">Abrindo a série…</p>}
      </section>
    );
  }

  // ===== a lista =====
  if (!aberta) {
    return (
      <section className="ced-serie" data-vista-serie="" data-serie-lista="">
        <span className="ced-eyebrow">Círculo · editor</span>
        <h1 className="ced-titulo">Séries</h1>
        <div className="ced-linha ces-topo">
          <button className="ced-btn ced-btn-1" data-acao="nova-serie" onClick={() => abrir('nova')}>{Ic.mais}<span>Nova série</span></button>
        </div>
        {falha
          ? <div className="ced-aviso"><p>{falha}</p><button className="ced-btn ced-btn-2" onClick={carregarLista}>Tentar de novo</button></div>
          : !lista ? <p className="ced-nota" aria-busy="true">Abrindo as séries…</p>
            : !lista.length ? <p className="ced-nota ces-vazio" data-series-vazio="">Nenhuma série ainda.</p>
              : (
                <div className="ces-lista" data-series="">
                  {lista.map((s) => (
                    <button key={s.id} className="ces-card" data-card-serie={s.id} onClick={() => abrir(s.id)}>
                      <CapaMini url={s.cover_url} titulo={s.livro?.titulo} />
                      <span className="ces-card-tx">
                        <b>{s.titulo}</b>
                        <small>{s.livro?.titulo}{s.livro?.autor ? `, de ${s.livro.autor}` : ''} · {s.episodios} {s.episodios === 1 ? 'episódio' : 'episódios'}</small>
                      </span>
                      <span className="ces-status" data-status={s.status}>{STATUS_CURTO[s.status] || s.status}</span>
                      <span className="ced-ir">{Ic.ir}</span>
                    </button>
                  ))}
                </div>
              )}
      </section>
    );
  }

  if (carregandoSerie) return <section className="ced-serie" data-vista-serie=""><p className="ced-nota" aria-busy="true">Abrindo a série…</p></section>;

  // ===== o formulário =====
  const nova = aberta === 'nova';
  const l = rasc.livro;
  const nT = contar(rasc.titulo), nD = contar(rasc.descricao);

  const seletorLivro = (
    <div className="ces-livro-escolha" data-livro-escolha="">
      <div className="ces-modos" role="tablist" aria-label="De onde vem o livro">
        {([['estante', 'Minha estante'], ['catalogo', 'Catálogo'], ['manual', 'Digitar']] as [Modo, string][]).map(([k, nome]) => (
          <button key={k} role="tab" aria-selected={modo === k} data-modo-livro={k}
            onClick={() => { setModo(k); setAchados(null); setErros((e) => { const n = { ...e }; delete n.livro; return n; }); }}>{nome}</button>
        ))}
      </div>
      {modo === 'manual' ? (
        <div className="ces-manual" data-livro-manual="">
          <input type="text" aria-label="Título do livro" placeholder="Título do livro" value={manual.titulo} data-manual="titulo"
            onChange={(e) => setManual((m) => ({ ...m, titulo: e.target.value }))} />
          <div className="ced-linha">
            <input type="text" aria-label="Autor" placeholder="Autor" value={manual.autor} data-manual="autor"
              onChange={(e) => setManual((m) => ({ ...m, autor: e.target.value }))} />
            <input type="text" inputMode="numeric" aria-label="Páginas (opcional)" placeholder="Páginas" className="ces-paginas" value={manual.paginas} data-manual="paginas"
              onChange={(e) => setManual((m) => ({ ...m, paginas: e.target.value }))} />
          </div>
          <button className="ced-btn ced-btn-2 ced-btn-sm" data-acao="usar-livro-manual" onClick={usarManual}>Usar este livro</button>
        </div>
      ) : (
        <div className="ces-busca">
          <span className="ces-busca-campo">{Ic.busca}
            <input type="search" autoComplete="off" value={busca} data-busca-livro={modo}
              aria-label={modo === 'estante' ? 'Buscar na sua estante' : 'Buscar no catálogo'}
              placeholder={modo === 'estante' ? 'Buscar na sua estante' : 'Título ou autor (3 letras ou mais)'}
              onChange={(e) => setBusca(e.target.value)} />
          </span>
          {buscando && <p className="ced-nota">Buscando…</p>}
          {achados && !buscando && (achados.length
            ? <ul className="ces-achados" data-achados={modo}>
              {achados.map((b, i) => (
                <li key={b.book_id || `${b.titulo}-${i}`}>
                  <button data-achado={i} onClick={() => escolherLivro({ book_id: b.book_id || null, titulo: b.titulo, autor: b.autor || '', paginas: b.paginas ? String(b.paginas) : '' })}>
                    {/* O autor em linha própria: no catálogo há várias edições
                        com o mesmo título, e é por ele que o Mentor distingue. */}
                    <b>{b.titulo}</b>
                    <span className="ces-achado-autor" data-achado-autor="">{b.autor || 'Autor não informado'}</span>
                    {(b.editora || b.ano || b.paginas) ? <small>{[b.editora, b.ano, b.paginas ? `${b.paginas} p.` : ''].filter(Boolean).join(' · ')}</small> : null}
                  </button>
                </li>
              ))}
            </ul>
            : <p className="ced-nota" data-achados-vazio="">{modo === 'estante' ? 'Nenhum livro da sua estante com esse nome.' : 'Nada encontrado no catálogo. Dá para digitar o livro.'}</p>)}
        </div>
      )}
      {l && <button className="ced-btn ced-btn-q ced-btn-sm" data-acao="manter-livro" onClick={() => setEscolhendo(false)}>Manter o livro escolhido</button>}
    </div>
  );

  const form = (
    <>
      <div className="ces-capa-linha" id="ces-cover_url" data-campo="cover_url">
        <CapaMini url={rasc.cover_url} titulo={l?.titulo} grande />
        <div className="ced-foto-acoes">
          <div className="ced-linha">
            <button className="ced-btn ced-btn-2 ced-btn-sm" data-acao="enviar-capa" disabled={enviandoCapa}
              onClick={() => arquivoRef.current?.click()}>{Ic.camera}{enviandoCapa ? 'Enviando…' : rasc.cover_url ? 'Trocar foto' : 'Enviar foto do exemplar'}</button>
            {rasc.cover_url && !enviandoCapa && <button className="ced-btn ced-btn-q ced-btn-sm" data-acao="tirar-capa" onClick={() => mudar('cover_url', null)}>Remover foto</button>}
          </div>
          {erroDe('cover_url')}
        </div>
        <input ref={arquivoRef} type="file" accept="image/*" hidden data-input-capa=""
          onChange={(e) => { escolherCapa(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      <div className="ced-campo" id="ces-livro" data-campo="livro" data-invalido={erros.livro ? '1' : undefined}>
        <label><span>Livro</span></label>
        {l && !escolhendo ? (
          <div className="ces-livro" data-livro-escolhido="">
            <span><b>{l.titulo}</b>{l.autor ? `, de ${l.autor}` : ''}{l.paginas ? ` · ${l.paginas} p.` : ''}
              <small>{l.book_id ? 'da sua estante' : 'do catálogo ou digitado'}</small></span>
            <button className="ced-btn ced-btn-q ced-btn-sm" data-acao="trocar-livro" onClick={() => { setEscolhendo(true); setModo('estante'); setBusca(''); }}>trocar</button>
          </div>
        ) : seletorLivro}
        {erroDe('livro')}
      </div>

      <div className="ced-campo" data-campo="titulo" data-invalido={erros.titulo ? '1' : undefined}>
        <label htmlFor="ces-titulo"><span>Título da série</span>{contador(nT, lim.titulo, 'titulo')}</label>
        <input id="ces-titulo" type="text" value={rasc.titulo} onChange={(e) => mudar('titulo', e.target.value)} />
        {erroDe('titulo')}
      </div>
      <div className="ces-duas">
        <div className="ced-campo" data-campo="cadencia" data-invalido={erros.cadencia ? '1' : undefined}>
          <label htmlFor="ces-cadencia"><span>Cadência</span></label>
          <select id="ces-cadencia" value={rasc.cadencia} onChange={(e) => mudar('cadencia', e.target.value)}>
            {CADENCIAS.map(([k, nome]) => <option key={k} value={k}>{nome}</option>)}
          </select>
          {erroDe('cadencia')}
        </div>
        <div className="ced-campo" data-campo="status" data-invalido={erros.status ? '1' : undefined}>
          <label htmlFor="ces-status"><span>Status</span></label>
          <select id="ces-status" value={rasc.status} onChange={(e) => mudar('status', e.target.value)}>
            {STATUS.map(([k, nome]) => <option key={k} value={k}>{nome}</option>)}
          </select>
          {erroDe('status')}
        </div>
      </div>

      {!assistente && (
        <details className="ced-mais" data-mais-detalhes="" open={maisAberto || !!erros.descricao || !!erros.loja_url}
          onToggle={(e) => setMaisAberto((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>{Ic.descer}<span>Mais detalhes</span>
            {(base?.descricao || base?.loja_url) && <small>{[base?.descricao && 'descrição', base?.loja_url && 'link'].filter(Boolean).join(' · ')}</small>}</summary>
          <div className="ced-campo" data-campo="descricao" data-invalido={erros.descricao ? '1' : undefined}>
            <label htmlFor="ces-descricao"><span>Descrição</span>{contador(nD, lim.descricao, 'descricao')}</label>
            <textarea id="ces-descricao" rows={3} value={rasc.descricao} onChange={(e) => mudar('descricao', e.target.value)} />
            {erroDe('descricao')}
          </div>
          <div className="ced-campo" data-campo="loja_url" data-invalido={erros.loja_url ? '1' : undefined}>
            <label htmlFor="ces-loja_url"><span>Link para comprar o livro</span></label>
            <input id="ces-loja_url" type="url" inputMode="url" value={rasc.loja_url} placeholder="https://…" onChange={(e) => mudar('loja_url', e.target.value)} />
            {erroDe('loja_url')}
          </div>
        </details>
      )}
    </>
  );

  const episodios = nova ? (
    <p className="ced-nota ces-eps-depois" data-eps-depois="">Os episódios entram depois de salvar a série.</p>
  ) : (
    <div className="ces-eps" data-episodios="">
      <ol className="ces-eps-lista">
        {eps.map((ep, i) => {
          const editando = editEp?.id === ep.id;
          const ocupado = ocupadoEp === ep.id;
          return (
            <li key={ep.id} className="ces-ep" data-ep={ep.id} data-posicao={ep.posicao} data-editando={editando ? '1' : undefined}>
              <span className="ces-ep-n">{ep.posicao}.</span>
              {editando ? (
                <div className="ces-ep-edit">
                  <div className="ced-campo">
                    <label htmlFor={`ces-ep-t-${ep.id}`}><span>Título do episódio</span>{contador(contar(editEp!.titulo), lim.episodio_titulo, 'ep-titulo')}</label>
                    <input id={`ces-ep-t-${ep.id}`} type="text" value={editEp!.titulo} placeholder="Título do episódio" data-ep-titulo=""
                      onChange={(e) => setEditEp((x) => x && { ...x, titulo: e.target.value })} />
                  </div>
                  <div className="ced-campo">
                    <label htmlFor={`ces-ep-s-${ep.id}`}><span>Sinopse</span>{contador(contar(editEp!.sinopse), lim.episodio_sinopse, 'ep-sinopse')}</label>
                    <textarea id={`ces-ep-s-${ep.id}`} rows={3} value={editEp!.sinopse} data-ep-sinopse=""
                      onChange={(e) => setEditEp((x) => x && { ...x, sinopse: e.target.value })} />
                  </div>
                  <div className="ced-linha ced-fim">
                    <button className="ced-btn ced-btn-q ced-btn-sm" onClick={() => setEditEp(null)} disabled={ocupado}>Cancelar</button>
                    <button className="ced-btn ced-btn-1 ced-btn-sm" data-acao="gravar-ep" onClick={gravarEp} disabled={ocupado}>{ocupado ? 'Gravando…' : 'Gravar'}</button>
                  </div>
                </div>
              ) : (
                <span className="ces-ep-tx"><b>{ep.titulo}</b>
                  {ep.sinopse ? <span className="ces-ep-sin">{ep.sinopse}</span> : null}
                  <small>{ep.texto}</small></span>
              )}
              {!editando && (
                <span className="ces-ep-acoes">
                  <button className="ced-icone" aria-label={`Editar o episódio ${ep.posicao}`} data-acao="editar-ep" disabled={!!ocupadoEp}
                    onClick={() => setEditEp({ id: ep.id, titulo: ep.titulo || '', sinopse: ep.sinopse || '' })}>{Ic.lapis}</button>
                  <span className="ced-mover">
                    <button aria-label={`Subir o episódio ${ep.posicao}`} data-acao="subir-ep" disabled={i === 0 || !!ocupadoEp} onClick={() => moverEp(i, -1)}>{Ic.subir}</button>
                    <button aria-label={`Descer o episódio ${ep.posicao}`} data-acao="descer-ep" disabled={i === eps.length - 1 || !!ocupadoEp} onClick={() => moverEp(i, 1)}>{Ic.descer}</button>
                  </span>
                  <button className="ced-icone ces-apagar" aria-label={`Apagar o episódio ${ep.posicao}`} data-acao="apagar-ep" disabled={!!ocupadoEp || ep.convertendo}
                    title={ep.convertendo ? 'O vídeo está convertendo: espere terminar para apagar.' : undefined} onClick={() => pedirApagar(ep)}>{Ic.lixo}</button>
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {!eps.length && <p className="ced-nota" data-eps-vazio="">Nenhum episódio ainda.</p>}
      <div className="ces-ep-novo">
        <input type="text" aria-label="Título do próximo episódio" placeholder="Título do episódio" value={novoEp} data-novo-ep=""
          onChange={(e) => setNovoEp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') adicionarEp(); }} />
        <button className="ced-btn ced-btn-2 ced-btn-sm" data-acao="adicionar-ep" disabled={!novoEp.trim() || !!ocupadoEp} onClick={adicionarEp}>
          {Ic.mais}<span>{ocupadoEp === 'novo' ? 'Adicionando…' : 'Adicionar'}</span></button>
      </div>
      {erroDe('eps')}
    </div>
  );

  // O que apagar leva junto (o servidor apaga em cascata).
  const avisosApagar = apagar ? [
    apagar.tem_video && 'O vídeo deste episódio será apagado.',
    apagar.assistido_por > 0 && (apagar.assistido_por === 1
      ? '1 pessoa começou a assistir; o progresso dela some.'
      : `${apagar.assistido_por} pessoas começaram a assistir; o progresso delas some.`),
    apagar.comentarios > 0 && `${apagar.comentarios} ${apagar.comentarios === 1 ? 'comentário será apagado' : 'comentários serão apagados'}.`,
  ].filter(Boolean) as string[] : [];

  return (
    <section className="ced-serie" data-vista-serie="" data-serie-form={nova ? 'nova' : aberta}>
      {!assistente && <>
        <button className="ced-btn ced-btn-q ced-btn-sm ces-voltar" data-acao="voltar-series" onClick={voltar}>{Ic.left}<span>Séries</span></button>
        <span className="ced-eyebrow">Círculo · editor · série</span>
        <h1 className="ced-titulo">{nova ? 'Nova série' : (base?.titulo || 'Série')}</h1>
      </>}

      {form}

      <div className="ced-secao-titulo"><h3>Episódios</h3>{!nova && <span>{eps.length}</span>}</div>
      {episodios}

      {assistente ? <>
        {erroDe('_geral')}
        {assistente.rodape({ pronto: !!rasc.livro && !!rasc.titulo.trim(), ocupado: salvando || enviandoCapa, avancar: avancarPasso })}
      </> : <div className="ced-salvar" data-salvar="">
        <div className="ced-salvar-estado" aria-live="polite">
          {salvo
            ? <span className="ced-salvo" role="status" data-salvo="">{Ic.ok}Salvo</span>
            : erros._geral ? <span className="ced-erro" role="alert" data-erro="_geral">{erros._geral}</span>
              : nova ? <span className="ced-nota">A série ainda não foi salva.</span>
                : temMudanca ? <span className="ced-nota">Há mudanças sem salvar.</span>
                  : <span className="ced-nota">Tudo salvo.</span>}
        </div>
        <button className="ced-btn ced-btn-1" data-acao="salvar-serie" onClick={salvar} disabled={!temMudanca || salvando || enviandoCapa}>
          {salvando ? 'Salvando…' : nova ? 'Criar série' : 'Salvar'}
        </button>
      </div>}

      {recorte && <Recorte img={recorte.img} onCancelar={fecharRecorte} onUsar={usarRecorte} proporcao={2 / 3} circulo={false}
        titulo="Recortar a capa" nota="Arraste para posicionar. O retângulo é a capa que vai para a página." />}
      {apagar && (
        <div className="ced-recorte" role="alertdialog" aria-modal="true" aria-label="Apagar o episódio" data-confirmar-apagar=""
          onClick={(e) => { if (e.target === e.currentTarget && ocupadoEp !== apagar.id) setApagar(null); }}>
          <div className="ced-recorte-cartao">
            <h2>Apagar o episódio {apagar.posicao}?</h2>
            <p className="ced-nota"><b>{apagar.titulo}</b> sai da série e os outros sobem de número. Não dá para desfazer.</p>
            {avisosApagar.length > 0 && <ul className="ces-avisos" data-avisos-apagar="">{avisosApagar.map((t) => <li key={t}>{t}</li>)}</ul>}
            <div className="ced-linha ced-fim">
              <button className="ced-btn ced-btn-q" onClick={() => setApagar(null)} disabled={ocupadoEp === apagar.id}>Cancelar</button>
              <button className="ced-btn ced-btn-1 ces-btn-perigo" data-acao="confirmar-apagar" onClick={confirmarApagar} disabled={ocupadoEp === apagar.id}>
                {ocupadoEp === apagar.id ? 'Apagando…' : 'Apagar'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
